// Catalog of widgets available on the main dashboard (components/main_dashboard.vue)
// plus the default show/hide set per role + tenant type. Kept framework-light
// (no Vue imports) so it's easy to unit test and reuse from the customize UI.
//
// A widget's default-on set is a starting point only: the user can hide any
// default widget and add any catalog widget regardless of role via the
// customize panel. Saved layouts always win over these defaults (see
// main_dashboard.vue's load order).

import { isFeatureEnabled } from './alerts_access'
import { ERM_ACCESS_ROLES } from './erm_access'

export const WIDGET_CATALOG = [
  {
    id: 'company_overview',
    name: 'Company Overview',
    description: 'Vehicle, driver, and user counts for your account.',
    icon: 'fa-building',
    category: 'Overview',
    width: 'full',
    tenantTypes: null,
    roles: null,
    // always pinned full-width at the top of the dashboard (see
    // main_dashboard.vue) -- not part of the customizable layout, so it's
    // excluded from getApplicableCatalog/getDefaultLayout/the add-widget
    // picker below. getWidgetDefinition still resolves it directly, for the
    // pinned header's icon/name.
    pinned: true,
  },
  {
    id: 'at_a_glance',
    name: 'At a Glance',
    description: "Today's trips, drivers and vehicles utilized, and this week's departures.",
    icon: 'fa-tachometer',
    category: 'Overview',
    // full width: row 2 is a seven-day chart, which needs the whole card
    width: 'full',
    // CM/TBN only -- the glance endpoints read those two services' booking
    // tables; FastTrak has no equivalent.
    tenantTypes: ['CM', 'TBN'],
    // every role but drivers, who get a card about the fleet's day when their
    // own dashboard should be about their own work -- and whose See-more link
    // lands on the actuals list they have no business in. no requiresFeature
    // either -- the endpoint reads bookings and the tenant record only, so there
    // is no actuals/alerts flag to gate on.
    roles: null,
    hiddenForRoles: ['driver'],
  },
  {
    id: 'quick_links',
    name: 'Quick Links',
    description: 'Shortcuts to the pages you use most.',
    icon: 'fa-th',
    category: 'Overview',
    width: 'half',
    tenantTypes: null,
    // hidden from drivers: the tiles are built from site_map_pages.js, whose
    // vsm/alerts/core/dispatch sections gate on feature flag only (no role
    // check), so a driver's Quick Links surface pages the nav rail hides from
    // them. hiddenForRoles keeps it out of the picker and strips it from any
    // saved driver layout. NOTE: this is the visible-path fix only -- those
    // routes still aren't blocked in the router guard, so a driver with the URL
    // can still reach them until site_map_pages.js/the guard are role-gated too.
    roles: null,
    hiddenForRoles: ['driver'],
  },
  {
    id: 'actuals_summary',
    name: 'Actuals Summary',
    description: 'Monthly actuals processing totals.',
    icon: 'fa-check-square-o',
    category: 'Bookings & Actuals',
    width: 'half',
    tenantTypes: ['CM', 'TBN', 'FASTTRAK'],
    roles: ['admin', 'customer-admin', 'customer-care', 'other', 'booking-customer'],
    requiresFeature: 'actuals_enabled',
  },
  {
    id: 'actuals_by_status',
    name: 'Actuals By Status',
    description: 'Daily actuals processing status, broken down by outcome.',
    icon: 'fa-bar-chart',
    category: 'Bookings & Actuals',
    width: 'half',
    tenantTypes: ['CM', 'TBN', 'FASTTRAK'],
    roles: ['admin', 'customer-admin', 'customer-care', 'other', 'booking-customer'],
    requiresFeature: 'actuals_enabled',
  },
  {
    id: 'alerts_summary',
    name: 'Alerts Summary',
    description: 'Alert counts broken down by type.',
    icon: 'fa-exclamation-circle',
    category: 'Alerts',
    width: 'half',
    tenantTypes: ['CM', 'TBN', 'FASTTRAK'],
    roles: ['admin', 'customer-admin', 'manager', 'dispatch', 'customer-care'],
    requiresFeature: 'alerts_enabled',
  },
  {
    id: 'sms_stats',
    name: 'SMS Stats',
    description: 'Monthly SMS send volume and cost.',
    icon: 'fa-commenting-o',
    category: 'Alerts',
    width: 'half',
    tenantTypes: ['CM', 'TBN', 'FASTTRAK'],
    roles: ['admin', 'customer-admin'],
    requiresFeature: 'alerts_enabled',
  },
  {
    id: 'notifications',
    name: 'Recent Notifications',
    description: 'Your latest in-app notifications and announcements.',
    icon: 'fa-bell-o',
    category: 'Overview',
    width: 'half',
    tenantTypes: null,
    roles: null,
  },
  {
    id: 'recent_alerts',
    name: 'Recent Alerts',
    description: 'The latest fleet alerts sent for your account.',
    icon: 'fa-bolt',
    category: 'Alerts',
    width: 'half',
    tenantTypes: ['CM', 'TBN', 'FASTTRAK'],
    roles: ['admin', 'customer-admin', 'manager', 'dispatch', 'customer-care'],
    requiresFeature: 'alerts_enabled',
  },
  {
    id: 'alerts_by_type',
    name: 'Alerts By Type',
    description: 'Alert volume broken down by type.',
    icon: 'fa-bar-chart',
    category: 'Alerts',
    width: 'half',
    tenantTypes: ['CM', 'TBN', 'FASTTRAK'],
    roles: ['admin', 'customer-admin', 'manager', 'dispatch', 'customer-care'],
    requiresFeature: 'alerts_enabled',
  },
  {
    id: 'delivery_status',
    name: 'Delivery Status',
    description: 'Email and SMS delivery success/failure counts.',
    icon: 'fa-paper-plane-o',
    category: 'Alerts',
    width: 'half',
    tenantTypes: ['CM', 'TBN', 'FASTTRAK'],
    roles: ['admin', 'customer-admin', 'customer-care'],
    requiresFeature: 'alerts_enabled',
  },
  {
    id: 'recent_delivery_errors',
    name: 'Recent Delivery Errors',
    description: 'The latest alerts that failed to deliver, and why.',
    icon: 'fa-exclamation-triangle',
    category: 'Alerts',
    width: 'half',
    tenantTypes: ['CM', 'TBN', 'FASTTRAK'],
    roles: ['admin', 'customer-admin', 'customer-care'],
    requiresFeature: 'alerts_enabled',
  },
  {
    id: 'vehicle_maintenance',
    name: 'Vehicle Status Manager',
    description: 'Not-ready vehicles departing soon, plus vehicles out of service.',
    icon: 'fa-wrench',
    category: 'Fleet',
    width: 'half',
    tenantTypes: null,
    roles: ['admin', 'customer-admin', 'manager', 'dispatch', 'garage'],
    requiresFeature: 'vehicle_status_management_enabled',
  },
  {
    id: 'ops_events',
    name: 'Event Response Manager',
    description: 'Open ops events awaiting a response, by status.',
    icon: 'fa-clipboard',
    category: 'Fleet',
    width: 'half',
    tenantTypes: null,
    // mirrors the erm router guard via the shared list; the tenant flag gates
    // the rest
    roles: ERM_ACCESS_ROLES,
    requiresFeature: 'ops_desk_enabled',
  },
  {
    id: 'coaching_portal',
    name: 'Coaching Portal',
    description: 'Open coaching tickets awaiting a conversation, with the escalated ones first.',
    icon: 'fa-graduation-cap',
    category: 'Drivers',
    width: 'half',
    tenantTypes: ['CM', 'TBN'],
    // the coaching page's own audience (a coaching record is a personnel
    // record, so the list is deliberately narrower than the fleet widgets);
    // the tenant flag gates it on top
    roles: ['admin', 'customer-admin', 'customer', 'manager'],
    requiresFeature: 'coaching_portal_enabled',
  },
  {
    id: 'driver_on_time',
    name: 'Driver On-Time %',
    description: 'On-time gate percentages (yard readiness, yard departure, first pickup) across the fleet.',
    icon: 'fa-clock-o',
    category: 'Bookings & Actuals',
    width: 'half',
    tenantTypes: ['CM', 'TBN'],
    roles: ['admin', 'customer-admin', 'manager', 'dispatch'],
    requiresFeature: 'actuals_enabled',
  },
  {
    id: 'driver_scorecard',
    name: 'Driver Manager',
    description: 'Fleet combined score, tier breakdown and headline KPIs for the last complete month.',
    icon: 'fa-id-badge',
    category: 'Drivers',
    width: 'half',
    tenantTypes: ['CM', 'TBN'],
    roles: ['admin', 'customer-admin', 'manager', 'dispatch'],
    requiresFeature: 'driver_scorecard_enabled',
  },
  {
    id: 'support_tickets',
    name: 'Recent Support Tickets',
    description: 'Your latest support tickets and their status.',
    icon: 'fa-life-ring',
    category: 'Overview',
    width: 'half',
    tenantTypes: null,
    roles: null,
    // help center (and its support-ticket tab) defaults ON like the site
    // map's core section -- only hidden if a tenant explicitly disables it.
    requiresFeatureNotFalse: 'help_center_enabled',
  },
]

function tenantContext(user) {
  const tenant = user && user.tenant
  const tenant_type = (user && user.types && tenant && user.types[tenant]) || null
  const features = (user && user.features && tenant && user.features[tenant]) || {}
  return { tenant, tenant_type, features }
}

// tenantTypes/requiresFeature(NotFalse)/hiddenForRoles gate whether a widget can
// appear at all (catalog + defaults + any saved layout that still names it);
// roles only gate the DEFAULT set, not availability -- so a widget a role must
// never see needs hiddenForRoles, not an omission from roles.
//
// requiresFeature demands the tenant flag be truthy (missing = off);
// requiresFeatureNotFalse only hides when the flag is explicitly false
// (missing = on). Both mirror the two gating styles already used in
// site_map_pages.js (e.g. alerts_enabled is opt-in, help_center_enabled is
// opt-out).
export function isWidgetApplicable(widget, user) {
  const { tenant_type, features } = tenantContext(user)
  if (widget.tenantTypes && !widget.tenantTypes.includes(tenant_type)) return false
  // through isFeatureEnabled, not raw truthiness: a tbn tenant storing the
  // STRING 'false' would otherwise get the widget while the nav hides the
  // section and the router guard bounces its See-more link
  if (widget.requiresFeature && !isFeatureEnabled(features[widget.requiresFeature])) return false
  if (widget.requiresFeatureNotFalse && features[widget.requiresFeatureNotFalse] === false) return false
  // hard exclusion: keeps the widget out of the Add Widget picker and strips it
  // from a saved layout that predates the rule (main_dashboard.vue filters saved
  // entries through this catalog), which `roles` alone would not do
  if (widget.hiddenForRoles && widget.hiddenForRoles.includes(user && user.role)) return false
  return true
}

function isDefaultForRole(widget, user) {
  if (!widget.roles) return true
  return widget.roles.includes(user && user.role)
}

// Catalog entries applicable to this user's tenant type/features, each
// tagged with whether it's part of their role's default set. Used by the
// "add widget" picker (which should still show non-default widgets).
// Pinned widgets (e.g. company_overview) are excluded -- they're not part
// of the customizable layout at all.
export function getApplicableCatalog(user) {
  return WIDGET_CATALOG
    .filter(widget => !widget.pinned && isWidgetApplicable(widget, user))
    .map(widget => ({ ...widget, isDefault: isDefaultForRole(widget, user) }))
}

export function getDefaultLayout(user) {
  return getApplicableCatalog(user)
    .filter(widget => widget.isDefault)
    .map(widget => ({ id: widget.id, visible: true }))
}

export function getWidgetDefinition(id) {
  return WIDGET_CATALOG.find(widget => widget.id === id) || null
}

// Folds default-on widgets the saved layout has never carried into it, at
// their default position. Without this a widget added to the catalog after a
// user last saved reaches only brand-new accounts -- everyone else has to
// find it in the Add Widget picker to learn it exists.
//
// Missing-entirely is the signal, and it's a safe one: the customize UI hides
// rather than removes, so a widget the user turned off is still in the saved
// list with visible:false and is left alone here. Only ids the user has never
// been offered are new.
export function mergeNewDefaults(saved, user) {
  const seen = new Set((saved || []).map(entry => entry.id))
  const merged = (saved || []).slice()
  getDefaultLayout(user).forEach((entry, index) => {
    if (!seen.has(entry.id)) merged.splice(index, 0, entry)
  })
  return merged
}
